import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getStatus } from "@/lib/api";
import { stripSurroundingQuotes, highlightTerms } from "@/lib/text";
import { TESTIDS } from "@/constants/testIds";
import { Loader2, CheckCircle2, MapPin } from "lucide-react";

const PLATFORMS = [
  { key: "chatgpt", label: "ChatGPT" },
  { key: "google_ai", label: "Google Search + AI" },
  { key: "perplexity", label: "Perplexity" },
];

// Property-anchored templates used only until real queries land at ~18% progress.
// {property} and {city} are replaced at render time.
const FALLBACK_QUERIES = [
  "Best boutique hotels in {city}",
  "Where should visitors stay near {property}",
  "Things to do in {city} for a weekend",
  "Restaurants near {property} for a special night out",
  "Weekend itinerary for {city} first-time visitors",
  "Historic hotels in {city} with character",
];

const CHECKLIST = [
  { threshold: 8, label: "Identifying your hotel and local demand generators" },
  { threshold: 18, label: "Building 24 property-specific travel questions" },
  { threshold: 30, label: "Testing answers across three discovery platforms" },
  { threshold: 60, label: "Evaluating who appears and how they're described" },
  { threshold: 82, label: "Calculating your Local Authority Score and topical breakdown" },
  { threshold: 92, label: "Identifying your highest-value content opportunities" },
  { threshold: 100, label: "Preparing your personalized report" },
];

export default function Grading() {
  const { analysisId } = useParams();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  const pollRef = useRef();

  useEffect(() => {
    const poll = async () => {
      try {
        const s = await getStatus(analysisId);
        setStatus(s);
        if (s.status === "complete") {
          setTimeout(() => nav(`/report/${analysisId}`), 900);
        } else if (s.status === "failed") {
          setError(s.status_message || s.error || "Analysis failed.");
        }
      } catch (_e) {
        setError("Could not fetch status.");
      }
    };
    poll();
    pollRef.current = setInterval(poll, 3500);
    return () => clearInterval(pollRef.current);
  }, [analysisId, nav]);

  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 1600);
    return () => clearInterval(t);
  }, []);

  const pct = status?.progress_pct ?? 0;
  const stage = status?.progress_stage || "Starting…";
  const message = status?.status_message || "";
  const complete = status?.status === "complete";
  const propName = status?.property?.name || "your hotel";
  const propCity = status?.property?.city || "your city";
  const propState = status?.property?.state || "";
  const propLabel = [propCity, propState].filter(Boolean).join(", ");

  // Real queries once generated; otherwise property-anchored fallbacks.
  const queryPool = useMemo(() => {
    const real = status?.queries || [];
    if (real.length >= 3) return real;
    return FALLBACK_QUERIES.map((t) =>
      t.replace("{city}", propCity).replace("{property}", propName)
    );
  }, [status?.queries, propCity, propName]);

  const currentQuery = stripSurroundingQuotes(
    queryPool[tick % Math.max(queryPool.length, 1)] || ""
  );

  // Terms we highlight in the active-query card — only the real values,
  // never the "your hotel"/"your city" placeholders used before status arrives.
  const highlightList = [status?.property?.name, status?.property?.city].filter(Boolean);
  const activePlatformIdx = tick % 3;

  // Personalized current-task copy per checklist stage.
  const currentTask = useMemo(() => {
    if (pct < 8) {
      return {
        title: "Identifying your hotel and local demand generators",
        detail: `Locating ${propName} on the map and pulling the demand generators, neighborhoods, and traveler types most relevant to ${propLabel || "your market"}.`,
      };
    }
    if (pct < 18) {
      return {
        title: "Building your property-specific query set",
        detail: `Identifying the traveler needs, local demand generators, and discovery questions most relevant to ${propName}.`,
      };
    }
    if (pct < 30) {
      return {
        title: "Building 24 property-specific travel questions",
        detail: `Each one written to reflect the queries a real-world traveler would make when planning a trip to the ${propCity !== "your city" ? propCity : "local"} area.`,
      };
    }
    if (pct < 60) {
      return {
        title: "Testing answers across three discovery platforms",
        detail: `Running each question through ChatGPT, Google AI Overviews, and Perplexity to see where ${propName} appears — and where someone else answers instead.`,
      };
    }
    if (pct < 82) {
      return {
        title: "Evaluating who appears and how they're described",
        detail: `Reviewing each response to determine whether ${propName} is the primary answer, a supporting mention, or absent — and when named, whether the tone is favorable, neutral, or ranked below competitors.`,
      };
    }
    if (pct < 92) {
      return {
        title: "Calculating your Local Authority Score and topical breakdown",
        detail: `Rolling up 24 queries × 3 platforms into your score, mapped across seven topical clusters and the authority tier of every publisher citing the ${propCity !== "your city" ? propCity : "local"} area.`,
      };
    }
    if (pct < 100) {
      return {
        title: "Identifying your highest-value content opportunities",
        detail: `Ranking the queries you're losing so ${propName} can start with the piece of content most likely to move the score.`,
      };
    }
    return {
      title: "Preparing your personalized report",
      detail: `Assembling the final report for ${propName}.`,
    };
  }, [pct, propName, propLabel, propCity]);

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="grading-page">
      {/* Header */}
      <div className="text-center mb-6 fade-in">
        <div className="label-eyebrow mb-3">Analysis in progress</div>
        <h1 className="font-serif text-3xl md:text-5xl leading-tight max-w-4xl mx-auto">
          Testing your hotel&apos;s local authority across ChatGPT, Google, and Perplexity.
        </h1>
        <p className="text-[color:var(--text-secondary)] mt-4 max-w-2xl mx-auto leading-relaxed">
          This usually takes 1&ndash;2 minutes. We&apos;re asking 24 travel-planning questions
          selected for your property and market, then evaluating whether your hotel &mdash; or
          someone else &mdash; owns each answer.
        </p>
      </div>

      {/* Persistent property banner */}
      {status?.property?.name && (
        <div
          className="flex items-center gap-3 justify-center mb-8 text-sm"
          style={{ color: "var(--text-secondary)" }}
          data-testid="grading-property-banner"
        >
          <MapPin className="w-4 h-4" style={{ color: "var(--brand-sage)" }} />
          <span>
            Analyzing{" "}
            <span
              className="font-serif italic text-base"
              style={{ color: "var(--text-primary)" }}
              data-testid="grading-property-name"
            >
              {propName}
            </span>
            {propLabel && <> · {propLabel}</>}
          </span>
        </div>
      )}

      {/* Progress bar */}
      <div className="glass rounded-2xl p-6 md:p-8 mb-6">
        <div className="flex items-baseline justify-between mb-3">
          <div
            className="font-serif italic text-lg"
            style={{ color: "var(--brand-sage-deep)" }}
            data-testid={TESTIDS.gradingStageLabel}
          >
            {stage}
          </div>
          <div
            className="font-serif text-3xl font-medium"
            style={{ color: "var(--text-primary)" }}
          >
            {pct}%
          </div>
        </div>
        <div className="progress-track" data-testid={TESTIDS.gradingProgressBar}>
          <div className="progress-fill" style={{ width: `${pct}%` }} />
        </div>
        {message && (
          <div className="mt-4 text-sm text-[color:var(--text-secondary)]">{message}</div>
        )}
      </div>

      {/* Current task — personalized */}
      <div
        className="rounded-2xl p-6 md:p-8 mb-6 fade-in"
        style={{
          background:
            "linear-gradient(135deg, rgba(107,122,85,0.08), rgba(166,124,63,0.04))",
          border: "1px solid rgba(107,122,85,0.28)",
        }}
        data-testid="grading-current-task"
        key={currentTask.title}
      >
        <div className="label-eyebrow mb-2">Current task</div>
        <div className="font-serif text-2xl md:text-3xl leading-tight mb-2">
          {currentTask.title}
        </div>
        <p className="text-[color:var(--text-secondary)] leading-relaxed max-w-3xl">
          {currentTask.detail}
        </p>
      </div>

      {/* Three-column live scanner — gets the visual weight */}
      <div className="grid md:grid-cols-3 gap-4 mb-6" data-testid="grading-platform-row">
        {PLATFORMS.map((p, i) => {
          const isActive = i === activePlatformIdx && !complete;
          return (
            <div
              key={p.key}
              className="rounded-2xl p-5 relative overflow-hidden transition-all duration-500"
              data-testid={TESTIDS.gradingPlatformCol(p.key)}
              style={
                isActive
                  ? {
                      background: "var(--bg-card)",
                      border: "1.5px solid rgba(107,122,85,0.55)",
                      boxShadow:
                        "0 12px 32px -12px rgba(107,122,85,0.35), 0 0 0 4px rgba(107,122,85,0.06)",
                      transform: "translateY(-2px)",
                    }
                  : complete
                    ? {
                        background: "var(--bg-card)",
                        border: "1px solid rgba(76,155,92,0.35)",
                      }
                    : {
                        background: "var(--bg-card-warm)",
                        border: "1px solid var(--border-default)",
                      }
              }
            >
              <div className="flex items-center justify-between mb-3">
                <div
                  className="label-eyebrow"
                  style={{
                    color: isActive
                      ? "var(--brand-sage-deep)"
                      : complete
                        ? "var(--text-primary)"
                        : "var(--text-secondary)",
                  }}
                >
                  {p.label}
                </div>
                {complete ? (
                  <CheckCircle2
                    className="w-4 h-4"
                    style={{ color: "var(--score-high)" }}
                  />
                ) : isActive ? (
                  <Loader2
                    className="w-4 h-4 animate-spin"
                    style={{ color: "var(--brand-sage)" }}
                  />
                ) : (
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ background: "var(--text-secondary)", opacity: 0.5 }}
                  />
                )}
              </div>
              <div className="min-h-[3.5rem]">
                {isActive ? (
                  <div
                    key={currentQuery}
                    className="query-preview text-[color:var(--text-primary)]"
                    data-testid="grading-active-query"
                  >
                    {highlightTerms(currentQuery, highlightList).map((seg, i) =>
                      seg.match ? (
                        <mark
                          key={i}
                          className="bg-transparent font-medium"
                          style={{
                            color: "var(--brand-sage-deep)",
                            textDecoration: "underline",
                            textDecorationColor: "rgba(107,122,85,0.55)",
                            textDecorationThickness: "1.5px",
                            textUnderlineOffset: "3px",
                          }}
                        >
                          {seg.text}
                        </mark>
                      ) : (
                        <span key={i}>{seg.text}</span>
                      )
                    )}
                  </div>
                ) : complete ? (
                  <span
                    className="text-sm italic"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    Done · 24 queries analyzed
                  </span>
                ) : (
                  <span
                    className="text-sm italic"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    Queued
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Checklist — compact, no per-item animation */}
      <div className="glass rounded-2xl p-5 md:p-6 mb-6">
        <div className="label-eyebrow mb-3">Overall progress</div>
        <ol className="space-y-2">
          {CHECKLIST.map(({ threshold, label }, idx) => {
            const done = pct >= threshold;
            const active = pct < threshold && (idx === 0 || pct >= CHECKLIST[idx - 1].threshold);
            return (
              <li key={threshold} className="flex items-center gap-3">
                {done ? (
                  <CheckCircle2
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: "var(--score-high)" }}
                  />
                ) : active ? (
                  <Loader2
                    className="w-4 h-4 animate-spin flex-shrink-0"
                    style={{ color: "var(--brand-sage)" }}
                  />
                ) : (
                  <div
                    className="w-4 h-4 rounded-full border-[1.5px] flex-shrink-0"
                    style={{ borderColor: "var(--text-secondary)", opacity: 0.55 }}
                  />
                )}
                <span
                  className="text-sm"
                  style={{
                    color: done
                      ? "var(--text-primary)"
                      : active
                        ? "var(--brand-sage-deep)"
                        : "var(--text-secondary)",
                    fontWeight: active ? 500 : 400,
                    opacity: !done && !active ? 0.85 : 1,
                  }}
                >
                  {label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Reassurance */}
      <div
        className="rounded-xl px-5 py-4 text-center text-sm leading-relaxed"
        style={{
          background: "var(--bg-card-warm)",
          border: "1px dashed var(--border-default)",
          color: "var(--text-secondary)",
        }}
        data-testid="grading-next-step"
      >
        <span className="font-medium" style={{ color: "var(--text-primary)" }}>
          Next:
        </span>{" "}
        See your score, your strongest existing authority signal, and the most valuable
        opportunity to improve.
      </div>

      {error && (
        <div
          className="mt-8 glass rounded-2xl p-6 border border-red-500/30 bg-red-500/5"
          data-testid="grading-error-panel"
        >
          <div className="text-[color:var(--score-low)] font-medium mb-1">
            Analysis failed
          </div>
          <div
            className="text-sm text-[color:var(--text-secondary)] mb-4"
            data-testid="grading-error-message"
          >
            {error}
          </div>
          <button
            type="button"
            onClick={() => nav("/")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--text-primary)] text-[color:var(--bg-primary)] text-sm font-medium hover:opacity-90 transition"
            data-testid="grading-retry-btn"
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );
}
